import { lazy, useState } from 'react';

import { FlowProvider } from '@sunflower-games/flow-react';
import get from 'lodash/get';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import './i18n/config';

import type { CMSContent } from './dto/cms.types';
import { useInactivityReload } from './hooks/useInactivityReload';
import Casino from './pages/Casino';
import Layout from './pages/Layout';
import Lobby from './pages/Lobby';
import MaintenancePage from './pages/Maintenance/Maintenance';
import { getRequest } from './services/api';
import { auth } from './services/auth';
import { useFlag } from './services/launchDarkly';
import type { ApiResponse } from './services/types';
import { APP_MOUNT_DATE } from './utils/appMount';

const flowUrl = import.meta.env.VITE_FLOW_URL;

// ❗️[Layout, Lobby and Casino] must be imported without lazy, because they are most likely to be used first
const Game = lazy(() => import('./pages/Game/Game'));
const Bingo = lazy(() => import('./pages/Bingo/BingoPage'));
const NotFound = lazy(() => import('./pages/errors/NotFound'));
const CMSPage = lazy(() => import('./pages/CMS/Pages'));

const rootRouter = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: '/',
        element: <Casino />,
        children: [
          {
            path: '/',
            element: <Lobby />,
          },
          {
            path: '/games/:gameId',
            element: <Game />,
          },
          {
            path: '/bingo',
            element: <Bingo />,
          },
        ],
      },
      {
        path: '/pages/:pageSlug',
        loader: async ({ params }) => {
          await auth.initCmsAuth();
          const response = await getRequest<ApiResponse<CMSContent>>('user/cms-detail', params);
          const payload = get(response, 'data.data', {});
          if (!payload) {
            throw new Error('CMS page not found');
          }
          return payload;
        },
        element: <CMSPage />,
        errorElement: <NotFound />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

export default function App() {
  const isMaintenance = useFlag<boolean>('maintenance', false);
  const isFlowEnabled = useFlag<boolean>('flow_kill_switch', false);
  const isFlowDebugEnabled = useFlag<boolean>('flow_debug_switch', false);

  const [bypassMaintenance, setBypassMaintenance] = useState(false);

  useInactivityReload();

  useEffectOnce(() => {
    window.addEventListener('message', (event) => {
      if (event.data === 'bypass-maintenance') {
        setBypassMaintenance(true);
      }
    });

    const sessionStartDate = sessionStorage.getItem('session-start-date');
    if (!sessionStartDate) {
      sessionStorage.setItem('session-start-date', APP_MOUNT_DATE);
    }
  });

  if (isMaintenance && !bypassMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <HelmetProvider>
      <FlowProvider baseUrl={flowUrl} debug={isFlowDebugEnabled} enabled={isFlowEnabled}>
        <RouterProvider router={rootRouter} />
      </FlowProvider>
    </HelmetProvider>
  );
}
